import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Badge } from '@savvy-kit/components/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import IconButton from '@savvy-kit/components/icon-button';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { ImageCard } from '@savvy-kit/patterns/image-card';
import { Tag } from '@savvy-kit/patterns/tag';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, DownloadIcon, EllipsisVerticalIcon, ExternalLinkIcon } from 'lucide-react';
import { graphql } from 'src/gql';
import { downloadObjectUrl } from 'src/utils/download-file';

import type { DownloadBoxFileUrlInput } from '@savvy/constants/generated/gql-types';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import type { BoxFileWithThumbnail } from './helpers';
import { formatFileName } from './helpers';

const DOWNLOAD_BOX_FILE = graphql(/* GraphQL */ `
  mutation DownloadBoxFileUrl($input: DownloadBoxFileUrlInput!) {
    downloadBoxFileUrl(input: $input) {
      fileName
      url
    }
  }
`);

function inferImageContentType(base64: string): string {
  if (typeof base64 !== 'string') return 'image/jpeg';
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  if (base64.startsWith('iVBORw')) return 'image/png';
  if (base64.startsWith('R0lGOD')) return 'image/gif';
  if (base64.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg';
}

const isRecent = (createdAt: string) => {
  if (!createdAt) return false;
  const createdDate = new Date(createdAt);
  const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
  return createdDate > twoWeeksAgo && createdDate <= new Date();
};

function AssetCardAction({
  boxFileId,
  fileFormat,
}: {
  readonly boxFileId: string | number;
  readonly fileFormat: string;
}) {
  const [downloadBoxFile] = useMutationWithErrorLogging<
    { downloadBoxFileUrl: { fileName: string; url: string } },
    { input: DownloadBoxFileUrlInput }
  >(DOWNLOAD_BOX_FILE);

  const handleOpenInNewTab = () => {
    window.open(`/advisor/dashboard/marketing/asset/${boxFileId}`, '_blank');
  };

  const handleDownload = async () => {
    const { data } = await downloadBoxFile({
      variables: {
        input: {
          boxFileId: boxFileId.toString(),
          fileFormat,
          dimensions: '',
        },
      },
    });

    if (!data?.downloadBoxFileUrl) {
      console.error('No data returned from downloadBoxFile', boxFileId);
      return;
    }

    const { url, fileName } = data.downloadBoxFileUrl;
    downloadObjectUrl({
      objectUrl: url,
      filename: `${fileName}.${fileFormat}`,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <IconButton
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        }
      >
        <EllipsisVerticalIcon />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={handleOpenInNewTab}>
          <ExternalLinkIcon />
          Open in new tab
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleDownload}>
          <DownloadIcon />
          Download
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ThumbnailPreview({ imageBase64 }: { readonly imageBase64: string | null }) {
  if (!imageBase64) {
    return <div className="size-full bg-slate-200" />;
  }

  const contentType = inferImageContentType(imageBase64);
  return (
    // eslint-disable-next-line @next/next/no-img-element -- thumbnail is built from runtime base64 asset data
    <img
      src={`data:${contentType};base64,${imageBase64}`}
      alt="Thumbnail"
      className="size-full object-cover object-top"
    />
  );
}

const CAROUSEL_NAV_BUTTON_CLASSES =
  'flex items-center justify-center size-8 rounded-full border border-border-light bg-background dark:bg-popover hover:bg-component-hover dark:hover:bg-component-hover transition-colors cursor-pointer';

function MarketingOverviewSection({
  children,
  title,
  headerAction,
}: {
  readonly children: React.ReactNode;
  readonly title: string;
  readonly headerAction?: React.ReactNode;
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const [canScroll, setCanScroll] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onResize = () => setCanScroll(emblaApi.canScrollNext() || emblaApi.canScrollPrev());
    onResize();
    emblaApi.on('resize', onResize);
    emblaApi.on('init', onResize);
    return () => {
      emblaApi.off('resize', onResize);
      emblaApi.off('init', onResize);
    };
  }, [emblaApi]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
        {canScroll && !headerAction && (
          <div className="flex items-center gap-1">
            <button type="button" onClick={scrollPrev} className={CAROUSEL_NAV_BUTTON_CLASSES}>
              <ChevronLeft className="size-4" />
            </button>
            <button type="button" onClick={scrollNext} className={CAROUSEL_NAV_BUTTON_CLASSES}>
              <ChevronRight className="size-4" />
            </button>
          </div>
        )}
        {headerAction}
      </div>
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">{children}</div>
      </div>
    </div>
  );
}

function ImageCardSkeleton() {
  return (
    <div className="w-[200px] rounded-xl border border-light bg-slate-100 overflow-hidden">
      <Skeleton className="h-[120px] w-full rounded-none" />
      <div className="p-4 bg-white space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-5 w-12 rounded-full" />
      </div>
    </div>
  );
}

export function MarketingOverviewAssetRow({
  title,
  emptyText,
  boxFilesWithThumbnails,
  loadingAssets,
  headerAction,
}: {
  readonly title: string;
  readonly emptyText?: string;
  readonly boxFilesWithThumbnails: BoxFileWithThumbnail[];
  readonly loadingAssets: boolean;
  readonly headerAction?: React.ReactNode;
}) {
  const router = useRouter();
  if (loadingAssets) {
    return (
      <MarketingOverviewSection title={title} headerAction={headerAction}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="shrink-0">
            <ImageCardSkeleton />
          </div>
        ))}
      </MarketingOverviewSection>
    );
  }

  if (boxFilesWithThumbnails.length === 0) {
    return (
      <MarketingOverviewSection title={title} headerAction={headerAction}>
        <div className="flex items-center justify-center w-full h-[200px] rounded-xl border border-light">
          <p className="text-sm text-secondary">{emptyText}</p>
        </div>
      </MarketingOverviewSection>
    );
  }

  return (
    <MarketingOverviewSection title={title} headerAction={headerAction}>
      {boxFilesWithThumbnails.map((entry) => {
        const fileName = entry.boxFile.name || '';
        const fileFormat = fileName.split('.').pop() || '';
        const createdAt = entry.boxFile.createdAt || '';

        return (
          <div key={entry.boxFile.id} className="shrink-0">
            <Link
              href={`${router.asPath}/asset/${entry.boxFile.id}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <ImageCard
                size="compact"
                title={formatFileName(fileName)}
                fileType={fileFormat.toUpperCase()}
                preview={<ThumbnailPreview imageBase64={entry.thumbnailBase64} />}
                action={<AssetCardAction boxFileId={entry.boxFile.id} fileFormat={fileFormat} />}
                badge={isRecent(createdAt) ? <Badge variant="primary">NEW</Badge> : undefined}
                tags={
                  entry.boxFile.tags?.length ? (
                    <>
                      {entry.boxFile.tags.slice(0, 2).map((tag) => (
                        <Tag key={tag} variant="read-only">
                          {tag}
                        </Tag>
                      ))}
                    </>
                  ) : undefined
                }
              />
            </Link>
          </div>
        );
      })}
    </MarketingOverviewSection>
  );
}
